import { Record, List } from "immutable";
import { IMeasurementMetadata } from "@tberrysoln/rsf-visualizer";
import { Nullable } from "common/records";
import { IImageData, ImageRecord, imageFromJSON } from "common/records/Image";
import { IAnnotationData, AnnotationRecord, annotationFromJSON } from "common/records/Annotation";
import { IWallFacadeData, WallFacadeRecord, wallFacadeFromJSON } from "common/records/WallFacade";
import { IMeasurementData, IMeasurementRecord } from "common/records/Measurement";

export const elevationFromJSON = (json: Partial<IElevationData>): ElevationRecord => {
  const recordData: IElevationRecord = { ...(json as any) };

  if (json.src_image) {
    recordData.src_image = imageFromJSON(json.src_image);
  }

  if (json.rendered_image) {
    recordData.rendered_image = imageFromJSON(json.rendered_image);
  }

  if (json.annotations) {
    recordData.annotations = new AnnotationsRecord({
      list: json.annotations.list ? List(json.annotations.list.map(annotationFromJSON)) : List<AnnotationRecord>(),
      wallFacades: json.annotations.wallFacades
        ? List(json.annotations.wallFacades.map(wallFacadeFromJSON))
        : List<WallFacadeRecord>(),
      measuring: json.annotations.measuring ?? null,
    });
  }

  if (json.removedAnnotations) {
    recordData.removedAnnotations = List(json.removedAnnotations.map(annotationFromJSON));
  }

  if (json.removedWallFacades) {
    recordData.removedWallFacades = List(json.removedWallFacades.map(wallFacadeFromJSON));
  }

  // Drop the nested measurement backref — the API returns IMeasurementData but
  // converting it here would force a circular import with Measurement.ts.
  // The measurement reducer hydrates the parent independently.
  delete (recordData as any).measurement;

  return new ElevationRecord(recordData);
};

export interface IElevationData {
  id: number;
  name: string;
  abbr: string;
  src_image_id: number;
  src_image: IImageData;
  rendered_image_id: number;
  rendered_image: IImageData;
  annotations: { list: IAnnotationData[]; wallFacades: IWallFacadeData[]; measuring: any };
  removedAnnotations: IAnnotationData[];
  removedWallFacades: IWallFacadeData[];
  measurement: IMeasurementData;
  measurement_id: number;
  $hasChanged: boolean;
}

// The annotations envelope is a typed nested Record (not a loose `Map<string, any>`), so consumers
// get `annotations.get("list")` → `List<AnnotationRecord>` / `.measuring` → metadata directly
// without per-call casts. `measuring` holds the serialized rsf-visualizer canvas state (or null
// before any measuring happens); `wallFacades` is the (currently RSF-25642-descoped) polygon list.
export interface IElevationAnnotations {
  list: List<AnnotationRecord>;
  wallFacades: List<WallFacadeRecord>;
  measuring: Nullable<IMeasurementMetadata>;
}

const defaultAnnotationsProps: IElevationAnnotations = {
  list: List<AnnotationRecord>(),
  wallFacades: List<WallFacadeRecord>(),
  measuring: null,
};

export class AnnotationsRecord extends Record(defaultAnnotationsProps) implements IElevationAnnotations {}

export interface IElevationRecord {
  id: number;
  name: string;
  abbr: string;
  src_image_id: number;
  src_image: ImageRecord;
  rendered_image_id: number;
  rendered_image: ImageRecord;
  annotations: AnnotationsRecord;
  removedAnnotations: List<AnnotationRecord>;
  removedWallFacades: List<WallFacadeRecord>;
  measurement: Nullable<IMeasurementRecord>;
  measurement_id: number;
  $hasChanged: boolean;
}

const defaultElevationProps: IElevationRecord = {
  id: 0,
  name: "",
  abbr: "",
  src_image_id: 0,
  src_image: new ImageRecord(),
  rendered_image_id: 0,
  rendered_image: new ImageRecord(),
  annotations: new AnnotationsRecord(),
  removedAnnotations: List<AnnotationRecord>(),
  removedWallFacades: List<WallFacadeRecord>(),
  measurement: null,
  measurement_id: 0,
  $hasChanged: false,
};

export class ElevationRecord extends Record(defaultElevationProps) implements IElevationRecord {}
